import { inject, Injectable } from '@angular/core';
import { spreadObjectStore$, Store } from '@sama/app-state';
import { assert, isDefined } from '@sama/js-core';
import { some } from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
import { isEmpty, isUndefined, pickBy } from 'lodash';
import { combineLatest, filter, mergeAll, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { AnnotationState } from '../../../cross-team-shared/annotation/shared/state/annotation-state';
import { ObjectState } from '../../../cross-team-shared/annotation/shared/state/object-state';
import {
  isShapeStore,
  TagsState,
} from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { assertStore } from '../../../cross-team-shared/misc-utils';
import {
  strictFind,
  strictSome,
} from '../../../cross-team-shared/misc-utils/strict-record';
import { AvailableAttributeService } from '../../../outputs/annotation/annotation-shared/utils/available-attribute.service';
import { Attributes } from '../../../team-apis/core/taxonomy';
import { Attribute } from '../../../team-apis/otterdam/taxonomy';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { getAttributeHelper } from '../../shared/attribute-helper/get-attribute-helper';
import { ObjectClassService } from '../../shared/misc-services/object-class.service';
import { ShapeService } from '../../shared/misc-services/shape.service';

@Injectable()
export class RequiredAnnotationsService extends InjectableSuperclass {
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private availableAttributeService = inject(AvailableAttributeService);
  private shapeService = inject(ShapeService);
  private objectClassService = inject(ObjectClassService);

  startListening() {
    this.listenToObjects();
    this.listenToShapes();
  }

  unsubmittableReason$() {
    const objectReason$ = this.taskWorkspaceStore('objectTransientInfo').$.pipe(
      map((transientInfo) => {
        const result = strictFind(
          transientInfo,
          (info) => info.hasMissingRequiredTags || info.hasMissingClass,
        );
        if (isUndefined(result)) {
          return undefined;
        }
        return result.hasMissingRequiredTags
          ? 'An object is missing a required tag'
          : 'An object is missing a class';
      }),
    );
    const shapeReason$ = this.taskWorkspaceStore('shapeTransientInfo').$.pipe(
      map((transientInfo) => {
        const result = strictSome(
          transientInfo,
          (info) => info.hasMissingRequiredTags,
        );
        return result ? 'A shape is missing a required tag' : undefined;
      }),
    );
    return combineLatest([objectReason$, shapeReason$]).pipe(
      map(([objectReason, shapeReason]) => objectReason || shapeReason),
    );
  }

  private listenToObjects() {
    this.subscribeTo(
      spreadObjectStore$(this.taskWorkspaceStore('objects')).pipe(
        switchMap((stores) => stores.map((store) => store.$)),
        mergeAll(),
        // when removing an object it could be undefined
        filter(isDefined),
      ),
      (object) => {
        this.updateHasMissingClass(object);
        const objectStore = this.taskWorkspaceStore.getObjectStore(
          object.index,
        );
        this.updateHasMissingRequiredAttribute(objectStore);
      },
    );
  }

  private listenToShapes() {
    this.subscribeTo(
      this.getShapeStores$().pipe(
        switchMap((stores) => stores.map((store) => store.$)),
        mergeAll(),
        // when removing a shape it could be undefined
        filter(isDefined),
      ),
      (shape) => {
        assert(isDefined(shape.parentId), 'shape must have an object id');
        this.updateHasMissingRequiredAttribute(
          this.shapeService.getShapeStore(shape.index),
        );
      },
    );
  }

  private getShapeStores$() {
    return spreadObjectStore$(
      this.taskWorkspaceStore('outputAnnotationInfo'),
    ).pipe(
      switchMap((outputStores) =>
        outputStores.map((outputStore) => {
          assertStore(outputStore, 'object must be defined');
          return spreadObjectStore$(outputStore('shapes'));
        }),
      ),
      mergeAll(),
    );
  }

  private updateHasMissingRequiredAttribute(
    annotationStore: Store<AnnotationState>,
  ) {
    const annotation = annotationStore.state();
    const attributes = this.availableAttributeService.getAttributes(annotation);
    if (isUndefined(attributes)) {
      return;
    }
    const hasMissingStaticAttribute =
      this.isAnnotationMissingRequiredStaticAttributes(annotation, attributes);

    const hasMissingDynamicAttribute =
      this.isAnnotationMissingRequiredDynamicAttributes(annotation, attributes);

    this.setAnnotationMissingRequiredTagsState(
      hasMissingStaticAttribute || hasMissingDynamicAttribute,
      annotationStore,
    );
  }

  private updateHasMissingClass(object: ObjectState) {
    const transientInfo = this.getObjectInfo(object.index);
    const hasValidClass = this.objectClassService.isValidClass(object.class);

    transientInfo('hasMissingClass').set(!hasValidClass);
  }

  private setAnnotationMissingRequiredTagsState(
    hasMissingTag: boolean,
    annotationStore: Store<AnnotationState>,
  ) {
    const transientInfoStore = this.getTransientInfoStore(annotationStore);
    transientInfoStore('hasMissingRequiredTags').set(hasMissingTag);
  }

  private getTransientInfoStore(annotationStore: Store<AnnotationState>) {
    return isShapeStore(annotationStore)
      ? this.getShapeInfo(annotationStore.state().index)
      : this.getObjectInfo(annotationStore.state().index);
  }

  private isAnnotationMissingRequiredStaticAttributes(
    annotation: TagsState,
    attributes: Attributes,
  ) {
    // tags can be undefined when the object is deleted
    if (isUndefined(annotation.tags)) {
      return false;
    }

    const staticAttributes = pickBy(
      attributes,
      (attribute) => attribute && !attribute.isDynamic,
    );

    return some(staticAttributes, (attribute) => {
      assert(attribute, 'attribute must be defined');
      return isAttributeValueInvalid(
        attribute,
        annotation.tags[attribute.name],
      );
    });
  }

  private isAnnotationMissingRequiredDynamicAttributes(
    annotation: TagsState,
    attributes: Attributes,
  ) {
    // dynamicTags can be undefined when the object is deleted
    if (isUndefined(annotation.dynamic_tags)) {
      return false;
    }

    const dynamicAttributes = pickBy(
      attributes,
      (attribute) => attribute && attribute.isDynamic,
    );

    return some(dynamicAttributes, (attribute) => {
      assert(attribute, 'attribute must be defined');
      const frameSpecificTags = annotation.dynamic_tags[attribute.name];
      const hasInvalid = some(frameSpecificTags, ({ value }) =>
        isAttributeValueInvalid(attribute, value),
      );
      return isEmpty(frameSpecificTags) || hasInvalid;
    });
  }

  private getObjectInfo(objectId: number) {
    const objectInfo = this.taskWorkspaceStore('objectTransientInfo')(objectId);
    assertStore(objectInfo, 'objectInfo must have transient info');
    return objectInfo;
  }

  private getShapeInfo(shapeId: number) {
    const shapeInfo = this.taskWorkspaceStore('shapeTransientInfo')(shapeId);
    assertStore(shapeInfo, 'Shape must have transient info');
    return shapeInfo;
  }
}

function isAttributeValueInvalid(attribute: Attribute, value: string) {
  const attributeHelper = getAttributeHelper(attribute.__type);
  return attributeHelper.isInvalidValue(value, attribute);
}
